import { useMemo, useEffect, useState } from 'react';
import type { PaymentProviderType, ShaliachPaymentProviderItem } from '@sm-dashboard/types';
import {
  isPaymentProviderCapableForDocumentType,
  PAYMENT_DOCUMENT_TYPES
} from '@sm-dashboard/types';
import { listShaliachPaymentProviders, getShaliachPaymentProvider } from '@/util/paymentProvidersApi';
import { useDataContext } from '@/context/DataContext';

export function usePaymentConfig(shaliachId?: number | null, enabled = true) {
  const { shaliach } = useDataContext();
  const [hasPaymentConfig, setHasPaymentConfig] = useState<boolean | null>(null);
  const [configuredProviderType, setConfiguredProviderType] = useState<PaymentProviderType | null>(null);
  const [providers, setProviders] = useState<ShaliachPaymentProviderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const cached =
    shaliachId != null && shaliach?.id === shaliachId ? shaliach.paymentProviders : undefined;

  useEffect(() => {
    if (!enabled) {
      setHasPaymentConfig(null);
      setConfiguredProviderType(null);
      setProviders([]);
      return;
    }
    if (!shaliachId) {
      setHasPaymentConfig(null);
      setConfiguredProviderType(null);
      setProviders([]);
      return;
    }

    if (cached) {
      const list = cached.providers ?? [];
      const configured = Boolean(cached.isConfigured) || list.length > 0;
      const defaultProvider = list.find(p => p.isDefault) ?? list[0];
      setProviders(list);
      setHasPaymentConfig(configured);
      setConfiguredProviderType(configured && defaultProvider?.providerType ? defaultProvider.providerType : null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    listShaliachPaymentProviders(Number(shaliachId))
      .then(data => {
        if (cancelled) return;
        const list = data?.providers ?? [];
        setProviders(list);
        const configured = Boolean(data?.isConfigured) || list.length > 0;
        setHasPaymentConfig(configured);
        const defaultProvider = list.find(p => p.isDefault) ?? list[0];
        setConfiguredProviderType(configured && defaultProvider?.providerType ? defaultProvider.providerType : null);
      })
      .catch(async err => {
        try {
          const data = await getShaliachPaymentProvider(Number(shaliachId));
          if (cancelled) return;
          const list = (data?.providers as ShaliachPaymentProviderItem[]) ?? [];
          setProviders(list);
          const configured = Boolean(data?.isConfigured) || list.length > 0;
          setHasPaymentConfig(configured);
          setConfiguredProviderType(configured && data?.providerType ? data.providerType : null);
        } catch {
          if (cancelled) return;
          setHasPaymentConfig(false);
          setConfiguredProviderType(null);
          setProviders([]);
          setError(err);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [shaliachId, enabled, cached]);

  const capableProvidersForTickets = useMemo(
    () =>
      providers.filter(
        p =>
          p.isActive !== false &&
          (isPaymentProviderCapableForDocumentType(
            p.supportedDocTypes,
            PAYMENT_DOCUMENT_TYPES.TaxInvoiceAndReceipt
          ) ||
            isPaymentProviderCapableForDocumentType(p.supportedDocTypes, PAYMENT_DOCUMENT_TYPES.Receipt))
      ),
    [providers]
  );

  /** No capable personal provider for ticket/receipt docs → UI shows system/Tzach notice. */
  const ticketSalesUseSystemProvider = useMemo(
    () => Boolean(hasPaymentConfig && capableProvidersForTickets.length === 0),
    [hasPaymentConfig, capableProvidersForTickets.length]
  );

  return {
    hasPaymentConfig,
    configuredProviderType,
    providers,
    capableProvidersForTickets,
    ticketSalesUseSystemProvider,
    loading,
    error
  };
}
